/**
 * The arithmetic behind dragging a row to a new place in a list.
 *
 * Separate from the component, and pure, because this is the part that is
 * wrong in a way you cannot see: a drag that lands one row off looks like a
 * shaky finger rather than like a bug, and nobody files it.
 *
 * Resting geometry only. Every function here is given where the rows sit when
 * nothing is being dragged, and the dragged row's centre where the finger has
 * put it. The rows the component slides out of the way are a consequence of
 * the answer, never an input to it — feeding the moved positions back in is
 * how a drag list starts oscillating between two indices under a still finger.
 */

/** moveItem returns the list with the item at `from` moved to `to`. */
export function moveItem<T>(items: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || from >= items.length) return items;
  const next = [...items];
  const [moved] = next.splice(from, 1);
  next.splice(Math.max(0, Math.min(items.length - 1, to)), 0, moved!);
  return next;
}

/**
 * dropIndex is the place a row dragged from `from` belongs, given where every
 * row rests and where the dragged one now is.
 *
 * It walks outward from `from` one row at a time rather than searching the
 * whole list, so a row only gives up its place once the dragged row's centre
 * has passed the centre of it. Testing against edges instead makes a tall row
 * swap as soon as a short one touches it, and the list flickers.
 */
export function dropIndex(centres: number[], from: number, draggedCentre: number): number {
  let to = from;
  while (to + 1 < centres.length && draggedCentre > centres[to + 1]!) to++;
  while (to > 0 && draggedCentre < centres[to - 1]!) to--;
  return to;
}

/**
 * shiftOf is how far row `i` slides while the row at `from` is being dragged
 * to `to`, in the units `sizes` is measured in.
 *
 * The rows between the two ends move by the height of the dragged row — not by
 * their own — because they are closing the gap it left, and that gap is its
 * size wherever it came from.
 */
export function shiftOf(sizes: number[], from: number, to: number, i: number): number {
  if (i === from) return 0;
  const moved = sizes[from] ?? 0;
  if (from < to && i > from && i <= to) return -moved;
  if (from > to && i >= to && i < from) return moved;
  return 0;
}
